import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { dataUser } from '../Helpers/util';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import dayjs from 'dayjs';

import axiosInstance from '../../utils/axiosInstance';
import SwOffCanvas from '../Common/SwOffCanvas/SwOffCanvas';
import SwButton from '../Common/SwButtons/SwButtons';
import SwMultiSelect from '../Common/SwMultiSelect/SwMultiSelect';
import AntDDatePicker from '../Common/AntDDateTimePicker/AntDDatePicker';

const userDataLocal = dataUser();
const UserObj = JSON.parse(userDataLocal);

const VisitorExport = ({ onClose }) => {
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
  });
  const [errors, setErrors] = useState({});
  const [loadingExport, setLoadingExport] = useState(false);
  const [showSlowMessage, setShowSlowMessage] = useState(false);

  useEffect(() => {
    if (!loadingExport) {
      setShowSlowMessage(false);
      return;
    }
    const timer = setTimeout(() => setShowSlowMessage(true), 10000);
    return () => clearTimeout(timer);
  }, [loadingExport]);

  // Date boundaries: from start of month 3 months ago to today (matches Access Log export)
  const today = dayjs().endOf('day');
  const threeMonthsAgoStart = dayjs()
    .subtract(3, 'month')
    .startOf('month');

  useEffect(() => {
    fetchLocationsBySpoc();
  }, []);

  const fetchLocationsBySpoc = () => {
    const authToken = JSON.parse(localStorage.getItem('headData'));
    const headers = {
      Authorization: authToken?.spintlyToken,
      'Content-Type': 'application/json',
    };
    axios
      .get(`${process.env.REACT_APP_API_URL}basic-data/get-location-by-spoc`, {
        params: { spoc: UserObj?.rec_id, client: UserObj?.company_id },
        headers,
      })
      .then(res => {
        if (res.data.status === 'success') {
          const formattedLocations = res?.data?.data?.map(location => ({
            label: location.location_name,
            value: location.rec_id,
          }));
          setLocations(formattedLocations);
        }
      })
      .catch(err => console.log('Error fetching locations:', err));
  };

  const handleSelectionChange = selectedOptions => {
    setSelectedLocations(selectedOptions);
  };

  const handleInputChange = (field, value) => {
    const dateValue = value ? dayjs(value) : null;

    setFilters(prev => {
      let newFilters = { ...prev, [field]: dateValue };

      // Auto-reset end date if start date > end date
      if (
        field === 'startDate' &&
        prev.endDate &&
        dateValue &&
        prev.endDate.isBefore(dateValue, 'day')
      ) {
        newFilters.endDate = null;
      }
      return newFilters;
    });
  };

  // ✅ Validation only for required fields and endDate < startDate
  const validateForm = (startDate, endDate, locations) => {
    let validationErrors = {};
    if (locations.length === 0)
      validationErrors.location = 'Please select at least one location';
    if (!startDate) validationErrors.startDate = 'Please choose start date';
    if (!endDate) validationErrors.endDate = 'Please choose end date';
    else if (startDate && endDate && endDate.isBefore(startDate, 'day'))
      validationErrors.endDate = 'End date cannot be earlier than start date';
    return validationErrors;
  };

  const resetForm = () => {
    setSelectedLocations([]);
    setFilters({ startDate: null, endDate: null });
    setErrors({});
  };

  const handleExport = async e => {
    e.preventDefault();

    const { startDate, endDate } = filters;
    const validation = validateForm(startDate, endDate, selectedLocations);

    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }

    try {
      setLoadingExport(true);

      const locationIds = selectedLocations.map(loc => loc.value);

      const payload = {
        company_name: UserObj.company_name,
        location_id: `[${locationIds.join(',')}]`,

        email: UserObj?.email,
        from_date: startDate.format('YYYY-MM-DD HH:mm:ss'),
        to_date: endDate.format('YYYY-MM-DD HH:mm:ss'),
      };

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_GATEWAYS_URL}reports/visit-logs/getReport`,
        payload,
      );

      if (response.status === 200) {
        toast.success(
          'Visitor logs export request sent. Please check your inbox.',
          { autoClose: 3000 }
        );

        resetForm();
        onClose(); // ✅ CLOSE MODAL
      } else {
        toast.error('Something went wrong while exporting logs.');
      }
    } catch (error) {
      console.error('Error exporting visitor logs:', error);
      toast.error('Export failed. Please try again later.');
    } finally {
      setLoadingExport(false);
    }
  };

  // Start Date: must be within the 90-day window
  const disabledStartDate = current => {
    if (!current) return false;
    return current < threeMonthsAgoStart || current > today;
  };
  // End Date is intentionally unconstrained so future-invited visitors
  // (whose entries are dated in the future) remain visible in the export.

  return (
    <SwOffCanvas
      show={true}
      onClose={() => {
        resetForm();
        onClose();
      }}
      title="Export Visitor Logs"
      placement="end"
      backdrop="static"
      keyboard={false}
      footer={
        <div className="OffCanvasFooter_Btns">
          <SwButton variant="dark-outline" onClick={resetForm}>
            Reset
          </SwButton>
          <SwButton
            variant="primary"
            className="ml10"
            onClick={handleExport}
            disabled={loadingExport}
          >
            {loadingExport ? 'Exporting...' : 'Export'}
          </SwButton>
        </div>
      }
    >
      {showSlowMessage && (
        <div className="row mb-2">
          <div className="col-md-12">
            <div className="SwAlert SwAlert-info">
              Loading is taking longer than expected. Kindly export the data.
            </div>
          </div>
        </div>
      )}
      {/* Location */}
      <div className="row">
        <div className="col-md-12">
          <div className="SwFormGroup">
            <label className="SwFormLabel">
              Location Name <span className='Required'>*</span>
            </label>
            <SwMultiSelect
              options={locations}
              value={selectedLocations}
              onChange={handleSelectionChange}
              labelledBy="Select locations"
            />
            {errors.location && (
              <span className="SwTextDanger SwTextSmall">{errors.location}</span>
            )}
          </div>
        </div>
      </div>

      {/* Start Date */}
      <div className="row">
        <div className="col-md-12">
          <div className="SwFormGroup">
            <label className="SwFormLabel">
              Start Date <span className='Required'>*</span>
            </label>
            <AntDDatePicker
              style={{ width: '100%' }}
              value={filters.startDate}
              format="YYYY-MM-DD hh:mm:ss A"
              showTime={{ use12Hours: true, format: 'hh:mm:ss A' }}
              placeholder="Select Start Date & Time"
              onChange={date => handleInputChange('startDate', date)}
              disabledDate={disabledStartDate}
            />
            {errors.startDate && (
              <span className="SwTextDanger SwTextSmall">{errors.startDate}</span>
            )}
          </div>
        </div>
      </div>

      {/* End Date */}
      <div className="row">
        <div className="col-md-12">
          <div className="SwFormGroup">
            <label className="SwFormLabel">
              End Date <span className='Required'>*</span>
            </label>
            <AntDDatePicker
              style={{ width: '100%' }}
              value={filters.endDate}
              format="YYYY-MM-DD hh:mm:ss A"
              showTime={{ use12Hours: true, format: 'hh:mm:ss A' }}
              placeholder="Select End Date & Time"
              onChange={date => handleInputChange('endDate', date)}
            />
            {errors.endDate && (
              <span className="SwTextDanger SwTextSmall">{errors.endDate}</span>
            )}
          </div>
        </div>
      </div>
    </SwOffCanvas>
  );
};

export default VisitorExport;